import {useEffect, useRef, useState} from "react";
import {
    DASHBOARD_SSE_EVENTS,
    normalizeSseArray,
    normalizeSseObject,
} from "../utils/dashboard-sse-events.js";

const API_DESPACHO = import.meta.env.VITE_API_DESPACHO;

const initialState = {
    serverDate: null,
    attendanceSubtitles: {},
    garrisonSubtitles: {},
    taskSubtitles: {},
    attendances: [],
    garrisons: [],
    tasks: [],
};

const prepareAttendanceCards = value => {
    const attendancesWithOpenChat = [];
    const otherAttendances = [];

    normalizeSseArray(value).forEach(attendance => {
        if (attendance.hasChat && !attendance.chatFinalized) {
            attendancesWithOpenChat.push(attendance);
            return;
        }

        otherAttendances.push(attendance);
    });

    return [...attendancesWithOpenChat, ...otherAttendances];
};

const useDashboardSse = (permissions = {}, options = {}) => {
    const [data, setData] = useState(initialState);
    const [lastEventAt, setLastEventAt] = useState(null);
    const [hasReceivedAttendanceCard, setHasReceivedAttendanceCard] = useState(false);
    const isPausedRef = useRef(false);
    const canReceiveAttendance = Boolean(permissions.attendance);
    const canReceiveGarrison = Boolean(permissions.garrison);
    const canReceiveTask = Boolean(permissions.task);

    useEffect(() => {
        isPausedRef.current = Boolean(options.isPaused);
    }, [options.isPaused]);

    useEffect(() => {
        setData(prev => ({
            ...prev,
            attendanceSubtitles: canReceiveAttendance ? prev.attendanceSubtitles : {},
            garrisonSubtitles: canReceiveGarrison ? prev.garrisonSubtitles : {},
            taskSubtitles: canReceiveTask ? prev.taskSubtitles : {},
            attendances: canReceiveAttendance ? prev.attendances : [],
            garrisons: canReceiveGarrison ? prev.garrisons : [],
            tasks: canReceiveTask ? prev.tasks : [],
        }));
    }, [canReceiveAttendance, canReceiveGarrison, canReceiveTask]);

    useEffect(() => {
        const eventSource = new EventSource(`${API_DESPACHO}/sse/subscribe`);

        eventSource.onmessage = event => {
            if (!event.data) return;

            try {
                const parsedData = JSON.parse(event.data);
                if (!parsedData?.type) return;

                setLastEventAt(new Date());
                setData(prev => {
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.SERVER_DATE) return {...prev, serverDate: parsedData.value};
                    if (isPausedRef.current) return prev;
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.ATTENDANCE_SUBTITLES && canReceiveAttendance) return {...prev, attendanceSubtitles: normalizeSseObject(parsedData.value)};
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.GARRISON_SUBTITLES && canReceiveGarrison) return {...prev, garrisonSubtitles: normalizeSseObject(parsedData.value)};
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.TASK_SUBTITLES && canReceiveTask) return {...prev, taskSubtitles: normalizeSseObject(parsedData.value)};
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.ATTENDANCE_CARD && canReceiveAttendance) return {...prev, attendances: prepareAttendanceCards(parsedData.value)};
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.GARRISON_CARD && canReceiveGarrison) return {...prev, garrisons: normalizeSseArray(parsedData.value)};
                    if (parsedData.type === DASHBOARD_SSE_EVENTS.TASK_CARD && canReceiveTask) return {...prev, tasks: normalizeSseArray(parsedData.value)};

                    return prev;
                });

                if (!isPausedRef.current && parsedData.type === DASHBOARD_SSE_EVENTS.ATTENDANCE_CARD && canReceiveAttendance) {
                    setHasReceivedAttendanceCard(true);
                }
            } catch (error) {
                console.error("Erro ao processar evento do dashboard:", error);
            }
        };

        eventSource.onerror = error => {
            console.error("Erro ao carregar dashboard:", error);
        };

        return () => eventSource.close();
    }, [canReceiveAttendance, canReceiveGarrison, canReceiveTask]);

    return {data, lastEventAt, hasReceivedAttendanceCard};
};

export default useDashboardSse;
